import { Box, Button, CircularProgress, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material'
import thankYouImg from '../../assets/thank-you-exam-completion.svg'
import React, { useCallback, useEffect, useState } from 'react'
import { erpResultsPageLink, Icons } from '../../utils/utilities'
import { useUserStore } from '../../store/user.store'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from '../../service/api'
import { toast } from 'react-toastify'
import failedImg from '../../assets/admissionexam-failed.svg'
import completedImg from '../../assets/completed.svg'
import successImg from '../../assets/success.svg'
import requestSentImg from '../../assets/sent.svg'
import errorImg from '../../assets/server-down.svg'
import aliveLogo from '../../assets/alivelogo.png'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import AlreadyAttempted from '../../components/admission-exam-components/candidate-attempt-info-blocks/AlreadyAttempted'
import AlreadyQualified from '../../components/admission-exam-components/candidate-attempt-info-blocks/AlreadyQualified'
import ReattemptRequested from '../../components/admission-exam-components/candidate-attempt-info-blocks/ReattemptRequested'
import ReattemptRequestApproved from '../../components/admission-exam-components/candidate-attempt-info-blocks/ReattemptRequestApproved'

const ThankYouPage = () => {

    const [attemptStatusDetails, setAttemptStatusDetails] = useState( {
        passed: false,
        failed: true,
        ongoing: false,
        noAttempt: false,
        allow_reattempt: false,
        reattempt_requested: true
    } )
    const [candidateDetails, setCandidateDetails] = useState( {} )
    const [loading, setLoading] = useState( true )
    const [mode, setMode] = useState( "thankyou" )

    const UserStore = useUserStore()

    const navigate = useNavigate()
    const { t } = useTranslation()
    const { palette } = useTheme()

    const goBack = () => {
        navigate( `/admission-exam/registration` )
    }

    const copyContactNumber = () => {
        navigator.clipboard.writeText( `+998553010009` )
            .then( () => {
                console.log( "Text copied to clipboard successfully!" )
            } )
            .catch( err => {
                console.error( "Failed to copy text: ", err )
            } )
    }

    const fetchLastAttemptResults = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: latestAttempt } } = await api.admissionExams.fetchLastAttemptResults()
            const attemptDetails = {
                passed: latestAttempt.participation_result_status === "QUALIFIED",
                ongoing: latestAttempt.participation_result_status === "NOT_SET",
                failed: latestAttempt.participation_result_status === "NOT QUALIFIED",
                noAttempt: false,
                allowReattempt: latestAttempt.allow_reattempt,
                reattemptRequested: latestAttempt.reattempt_requested
            }
            setCandidateDetails( latestAttempt?.candidate_details || {} )
            setAttemptStatusDetails( attemptDetails )
            if ( attemptDetails.ongoing ) {
                setMode( "ongoing" )
            } else if ( attemptDetails.passed ) {
                setMode( "passed" )
            } else if ( attemptDetails.failed && attemptDetails.allowReattempt && !attemptDetails.reattemptRequested ) {
                setMode( "reattempt" )
            } else if ( attemptDetails.failed && !attemptDetails.allowReattempt && attemptDetails.reattemptRequested ) {
                setMode( "reattempt-requested" )
            } else if ( attemptDetails.failed && ( !attemptDetails.allowReattempt || ( attemptDetails.allowReattempt && attemptDetails.reattemptRequested ) ) ) {
                setMode( "failed" )
            }
        } catch ( err ) {
            if ( err?.response?.status === 404 || err?.response?.status === 401 || err?.response?.status === 403 ) {
                setMode( "thankyou" )
            } else {
                setMode( "error" )
                toast( err?.response?.data?.message || err?.response?.data || "Something went wrong", { containerId: "main" } )
            }
        } finally {
            setLoading( false )
        }
    }, [] )

    useEffect( () => {
        fetchLastAttemptResults()
    }, [fetchLastAttemptResults] )

    return (
        <Box sx={{ background: `url('/imgs/bg-pattern1.png')` }} display="flex" flexGrow={1} alignItems="safe center" justifyContent="center" padding="20px" overflow="auto">
            {mode === "thankyou" && <Paper sx={{ minWidth: "220px", padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", maxWidth: "400px" }}>
                <Box component="img" width="250px" src={thankYouImg} alt="Alive" />
                <Typography variant="subtitle2" display="flex" alignItems="center" gap="10px">{t( "message.submittedSuccessfully" )}</Typography>

                {loading && <DataLoadingSpinner size={20} waitingMessage={t( "message.fetchingAttemptDetails" )} />}
            </Paper>}

            {mode === "error" && <Paper sx={{ minWidth: "220px", padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", maxWidth: "400px" }}>
                <Box component="img" width="150px" src={failedImg} alt="Alive" />
                <Typography variant='h6' lineHeight={1.2} textAlign="center" sx={{ fontSize: "0.875rem" }}>{t( "message.fetchingAttemptDetailsError" )}</Typography>
                <Button disabled={loading} variant='contained' color='errorMessage' onClick={fetchLastAttemptResults} disableElevation startIcon={loading ? <CircularProgress size={14} /> : Icons.default.Replay} sx={{ textTransform: "capitalize" }} >{t( "button.retry" )}</Button>

            </Paper>}
            {mode === "ongoing" && <Paper sx={{ minWidth: "220px", padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", maxWidth: "400px" }}>

                <Box component="img" src={thankYouImg} width="200px" alt="Attempt already made" />

                <Typography variant='h6' lineHeight={1.2} textAlign="center" sx={{ fontSize: "1.3rem" }}>{t( "thankYou.ongoing" )}</Typography>
                <Box display="flex" flexDirection="column" gap="10px" flexWrap="wrap">
                    <Button fullWidth variant='contained' disableElevation sx={{ textTransform: "capitalize" }} endIcon={Icons.default.ArrowForward} color='primary' onClick={goBack} >{t( "button.completeExam" )}</Button>
                </Box>
            </Paper>}

            {mode === "failed" && <AlreadyAttempted handleReattemptRequest={goBack} />}

            {mode === "passed" && <AlreadyQualified programId={candidateDetails.program_id} applicationNumber={candidateDetails.application_number} />}

            {mode === "reattempt" && <ReattemptRequestApproved onCta={goBack} />}

            {mode === "reattempt-requested" && <ReattemptRequested onBack={goBack} />}
        </Box>
    )
}

export default ThankYouPage
